import { ExpandableText } from "@ui/components/ExpandableText";
import { Icon } from "@ui/components/Icon";
import { tv } from "@ui/lib/tv";

const experienceCard = tv({
  slots: {
    root: "flex items-start gap-3 lg:gap-5",
    imageWrapper: "shrink-0 pt-1 lg:pt-0",
    image: "size-[72px] rounded-lg bg-neutral-25 object-cover lg:size-[180px] md:rounded-xl",
    content: "flex min-w-0 flex-1 flex-col gap-2",
    title: "text-lg font-semibold text-neutral-900 lg:text-xl",
    metaGroup: "flex flex-col gap-1",
    metaRow: "flex items-center gap-2",
    metaText: "text-sm text-neutral-900 lg:text-base",
    description: "text-sm text-neutral-600 lg:text-base",
  },
});

/** Displays a single teaching or professional experience entry with image, metadata, and expandable description. */
export interface ExperienceCardProps {
  /** Date range of the experience (e.g. "Sep 2024 – Present"). */
  dates: string;
  /** Full description text, truncated with ExpandableText. */
  description: string;
  /** URL for the experience image. */
  image: string;
  /** Location where the experience took place. */
  location: string;
  /** Role title held during the experience. */
  role: string;
  /** Name of the school or organization. */
  school: string;
}

export function ExperienceCard({ dates, description, image, location, role, school }: ExperienceCardProps) {
  const styles = experienceCard();

  return (
    <div className={styles.root()}>
      <div className={styles.imageWrapper()}>
        <img alt={school} className={styles.image()} src={image} />
      </div>
      <div className={styles.content()}>
        <h3 className={styles.title()}>{role}</h3>
        <div className={styles.metaGroup()}>
          <div className={styles.metaRow()}>
            <Icon className="text-neutral-600 lg:hidden" name="bookOpen" size="16" />
            <Icon className="hidden text-neutral-600 lg:block" name="bookOpen" size="20" />
            <span className={styles.metaText()}>{school}</span>
          </div>
          <div className={styles.metaRow()}>
            <Icon className="text-neutral-600 lg:hidden" name="date" size="16" />
            <Icon className="hidden text-neutral-600 lg:block" name="date" size="20" />
            <span className={styles.metaText()}>{dates}</span>
          </div>
          <div className={styles.metaRow()}>
            <Icon className="text-neutral-600 lg:hidden" name="location" size="16" />
            <Icon className="hidden text-neutral-600 lg:block" name="location" size="20" />
            <span className={styles.metaText()}>{location}</span>
          </div>
        </div>
        <ExpandableText className={styles.description()} lines={5}>
          {description}
        </ExpandableText>
      </div>
    </div>
  );
}
